import { PayloadAction } from '@reduxjs/toolkit';
import {
  ConfigSaga,
  ConfigSelectors,
  ExperimentsSelectors,
  IDomainCalculation,
  LanguageService,
  NotificationActions,
  ProgressActions,
  ProgressSelectors,
  StatsSelectors,
  logger,
  waitForFullProgressSaga,
} from '@web_opxp/features';
import { all, call, put, select, spawn, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { ApiService } from 'web_core_library';
import { AssessmentActions } from '../assessment';
import {
  ASSESSMENT_SESSION_ID,
  CFQ_DOMAINS,
  LAST_PROGRESS_RESET,
  MOOD_ID,
  PROGRESS_RESET_EVENT_COUNT,
  TRAINING_DOMAINS,
} from '../base/constants';
import { EDomain } from '../base/types';
import { CyclesActions, CyclesSelectors, CyclesTypes } from '../cycles';
import { ExercisesActions } from '../exercises';
import EvaluationService from './EvaluationService';
import { CFQ_DOMAIN_EVALUATION_LIMIT, TRAINING_DOMAIN_EVALUATION_LIMIT } from './constants';
import { isFeatureReady } from './selectors';
import { EvaluationActions } from './slice';
import {
  DomainsCalculationApiResponse,
  IDomainProgress,
  ILoadEvaluationHighscoresPayload,
  ILoadEvaluationProgressHistoryPayload,
  ILoadEvaluationsHighscoresForSingleExercisePayload,
} from './types';
import * as Utils from './utils';

export function* openEvaluationSaga(action: PayloadAction<number | undefined>) {
  const userId = action.payload;
  // load exercises by domains
  yield put(ExercisesActions.initExercises(userId));
  // ensure that feature is initialized
  yield call(waitForReady);
  // load data needed for first tab
  yield call(preloadMoodProgress, userId);
  yield call(loadEvaluationForDomains, userId);
}

export function* loadEvaluationForDomains(userId?: number) {
  // retrieve domain progress from backend
  for (const domain of TRAINING_DOMAINS) {
    yield spawn(loadCalculationHistoryByDomain, domain, userId);
  }
  for (const domain of CFQ_DOMAINS) {
    yield spawn(LoadSelfAssessmentCalculationsByDomain, domain, userId);
  }
}

export function* waitForReady() {
  const isReady = yield select(isFeatureReady);
  if (!isReady) {
    yield take(EvaluationActions.evaluationReady);
  }
}

export function* preloadMoodProgress(userId?: number) {
  const isMoodTrackerEnabled = yield select(ExperimentsSelectors.isExperimentEnabled, 7428);
  if (!isMoodTrackerEnabled) {
    return;
  }

  // we must wait for progress feature to be ready
  const isReady: boolean = yield select(ProgressSelectors.isProgressReady);
  if (!isReady) {
    yield take(ProgressActions.progressReady);
  }

  yield put(ProgressActions.loadWorkoutsProgress([MOOD_ID], userId));
  // NOTE: probably have sense to check for progress in the state
  // to make sure that update action belongs to our request
  // or we will need to wait for full progress here?
  yield take(ProgressActions.updateWorkoutProgress);
}

export function* initEvaluationSaga() {
  //init api
  yield call(EvaluationService.init, ApiService);
  yield put(EvaluationActions.evaluationReady());
}

export function* getCycleEvaluationSaga(action: PayloadAction<number>) {
  const cycleId = action.payload;
  // retrive data from backend
  const apiResponse: DomainsCalculationApiResponse = yield call(EvaluationService.getCycleEvaluation, cycleId);
  const { domaincalculation } = apiResponse.data;

  // send result to caller
  yield put(EvaluationActions.cycleEvaluationResult(domaincalculation));
}

export function* loadCalculationHistoryByDomain(domain: number, userId?: number) {
  // retrive data from backend
  const apiResponse: DomainsCalculationApiResponse = yield call(
    EvaluationService.getUserEvaluation,
    domain,
    TRAINING_DOMAIN_EVALUATION_LIMIT, // max count of results is 11 according to design
    userId
  );
  const { domaincalculation } = apiResponse.data;

  // change domaincalculation data format, so we can use it for histogram
  const result = domaincalculation.map(({ cycleId, value, timestamp }) => ({
    cycleId,
    normalizedScore: value,
    time: timestamp,
  }));
  // save domain Progress to store
  yield put(EvaluationActions.updateEvaluationProgress(result, domain));
  // trigger integrity check only if its own evaluation and domain === 1
  if (typeof userId === 'undefined' && domain === 1) {
    yield spawn(domainEvaluationIntegrityCheck, domain, result);
  }
}

export function* LoadSelfAssessmentCalculationsByDomain(domain: number, userId?: number) {
  // retrive data from backend
  const apiResponse: DomainsCalculationApiResponse = yield call(
    EvaluationService.getUserEvaluation,
    domain,
    CFQ_DOMAIN_EVALUATION_LIMIT, // max count of results is 6 according to design
    userId
  );
  const { domaincalculation } = apiResponse.data;

  // sort results by timestamp
  const sortedResults: IDomainCalculation[] = yield call(Utils.sortByTimestamp, domaincalculation);
  // normalize scores
  const result = sortedResults.map(Utils.normalizeCFQScore);
  // save domain Progress to store
  yield put(EvaluationActions.updateEvaluationProgress(result, domain));
}

export function* waitForCycles() {
  // wait until cycles state are loaded
  const cyclesLoading = yield select(CyclesSelectors.isCyclesLoading);
  if (cyclesLoading) {
    yield take(CyclesActions.cyclesReady);
  }
}

export function* domainEvaluationIntegrityCheck(domain: number, progress: IDomainProgress[]) {
  if (domain !== 1) {
    // skip all other domains while they will have the same issue as the first one
    return;
  }
  yield call(waitForCycles);
  yield call(ConfigSaga.preloadConfig);
  yield call(logger, `[IC] integrity check for domain ${EDomain[domain]}`);
  const sortedProgress = Utils.sortDomainProgressByCycle(progress);
  yield call(logger, ["[IC] domain progress cycle id's:", [...sortedProgress].map((p) => `${p.cycleId}`).join(',')]);
  const currentCycleId = yield select(CyclesSelectors.getCurrentCycleId);
  let minCycleId = Math.max(currentCycleId - TRAINING_DOMAIN_EVALUATION_LIMIT + 1, 1);
  let maxCycleId = currentCycleId;
  yield call(logger, `[IC] initial ids: minCycleId=${minCycleId}, maxCycleId=${maxCycleId}`);
  if (sortedProgress.length > 0) {
    minCycleId = Math.min(sortedProgress[0].cycleId, minCycleId);
    // current cycle can not be less than the one in progress data, so this is probably not needed
    maxCycleId = Math.max(sortedProgress[sortedProgress.length - 1].cycleId, currentCycleId);
    // in case of current cycle is less then the one in calculations (see BW-6382)
    minCycleId = Math.max(maxCycleId - TRAINING_DOMAIN_EVALUATION_LIMIT + 1, minCycleId);
  }
  yield call(logger, `[IC] ids after progress analysis: minCycleId=${minCycleId}, maxCycleId=${maxCycleId}`);
  let notificationShown = false;
  const t: ReturnType<typeof LanguageService.getTFunction> = yield call(LanguageService.getTFunction);
  for (let cycleId = maxCycleId; cycleId >= minCycleId; cycleId--) {
    // check if domain calculation is present for given cycle
    const calculationPresent = Utils.isDomainProgressPresentForCycle(sortedProgress, cycleId);
    yield call(logger, `[IC] cycleId=${cycleId} check, calculationPresent=${calculationPresent}`);
    if (calculationPresent) {
      continue;
    }
    // if calculation not present check if assessment session present for given cycle and properly completed
    yield call(preloadAssessmentSessionForCycle, cycleId);
    const assessmentWorkoutIds: Array<number> = yield select(ConfigSelectors.getAssessmentWorkoutIds);
    const assessmentCompleted: boolean = yield select(
      CyclesSelectors.wasAssessmentFullyCompleted,
      cycleId,
      assessmentWorkoutIds
    );
    yield call(logger, `[IC] cycleId=${cycleId} check, wasAssessmentFullyCompleted=${assessmentCompleted}`);
    if (!assessmentCompleted) {
      continue;
    }
    const resetValue: number | undefined = yield select(StatsSelectors.getStatIntValueById, PROGRESS_RESET_EVENT_COUNT);
    const hasProgressBeenReset = resetValue && resetValue >= 1;
    if (hasProgressBeenReset) {
      // Progress has been reset, need to skip older cycles
      const progressResetTimestamp: number | undefined = yield select(
        StatsSelectors.getStatIntValueById,
        LAST_PROGRESS_RESET
      );
      const asessmentTimestamp: number = yield select(CyclesSelectors.getAssessmentFinishTimestamp, cycleId);
      if (
        progressResetTimestamp &&
        progressResetTimestamp > 0 &&
        asessmentTimestamp > 0 &&
        asessmentTimestamp <= progressResetTimestamp
      ) {
        continue;
      }
    }
    if (!notificationShown) {
      yield put(NotificationActions.showWarning(t('opxp.training:Evaluation.IntegrityCheck.IssueFound')));
      notificationShown = true;
    }
    // initiate cycle evaluation
    yield put(AssessmentActions.cycleEvaluation(cycleId));
    yield take(AssessmentActions.cycleEvaluationComplete);
  }
  if (notificationShown) {
    // reload domain calculations
    yield call(loadEvaluationForDomains);
    // report success
    yield put(NotificationActions.showSuccess(t('opxp.training:Evaluation.IntegrityCheck.IssueFixed')));
  }
}

export function* preloadAssessmentSessionForCycle(cycleId: number) {
  yield call(logger, `[IC] cycleId=${cycleId} check, preloading assessment session`);
  const assessment: CyclesTypes.ISessionClientStatus | undefined = yield select(
    CyclesSelectors.getCycleSession,
    cycleId,
    ASSESSMENT_SESSION_ID
  );
  if (!assessment) {
    // if assessment not present in state - try to load it from backend
    yield put(CyclesActions.loadAssessment(cycleId));
    yield take(CyclesActions.loadAssessmentComplete);
  }
}

export function* loadHighscoreSaga(action: PayloadAction<ILoadEvaluationHighscoresPayload>) {
  const { workoutIds, userId } = action.payload;
  const apiResponse = yield call(EvaluationService.getHighscore, workoutIds, userId);
  yield put(EvaluationActions.updateEvaluationHighscores(apiResponse.data));
}

export function* loadHighscoreForSingleExerciseSaga(
  action: PayloadAction<ILoadEvaluationsHighscoresForSingleExercisePayload>
) {
  const { workoutIds, userId } = action.payload;
  const apiResponse = yield call(EvaluationService.getHighscore, workoutIds, userId);
  yield put(EvaluationActions.updateEvaluationSingleExerciseHighscores(apiResponse.data));
}

export function* loadProgressSaga(action: PayloadAction<number | undefined>) {
  const userId = action.payload;
  yield call(waitForFullProgressSaga);
  yield put(ProgressActions.loadProgress(userId));
  yield take(ProgressActions.progressLoaded);
}

export function* initPerformancePageSaga(action: PayloadAction<number | undefined>) {
  const userId = action.payload;
  const workoutIds: number[] = yield select(ConfigSelectors.getAllSupportedExercisesWorkoutIds);
  yield put(EvaluationActions.loadProgress(userId));
  yield put(EvaluationActions.loadEvaluationHighscores(workoutIds, userId));
}

export function* loadProgressHistorySaga(action: PayloadAction<ILoadEvaluationProgressHistoryPayload>) {
  const { startTimestamp, endTimestamp, userId } = action.payload;
  const isReady: boolean = yield select(ProgressSelectors.isProgressReady);
  if (!isReady) {
    yield take(ProgressActions.progressReady);
  }
  yield put(ProgressActions.loadProgressHistory(startTimestamp, endTimestamp, userId));
  yield take(ProgressActions.updateProgressHistory);
}

export default function* evaluationWatcher() {
  yield all([
    takeEvery(EvaluationActions.openEvaluation, openEvaluationSaga),
    takeEvery(EvaluationActions.initEvaluation, initEvaluationSaga),
    takeEvery(EvaluationActions.getCycleEvaluation, getCycleEvaluationSaga),
    takeEvery(EvaluationActions.loadEvaluationHighscores, loadHighscoreSaga),
    takeEvery(EvaluationActions.loadEvaluationsHighscoresForSingleExercise, loadHighscoreForSingleExerciseSaga),
    takeEvery(EvaluationActions.loadProgress, loadProgressSaga),
    takeLatest(EvaluationActions.loadProgressHistory, loadProgressHistorySaga),
    takeLatest(EvaluationActions.initPerformancePage, initPerformancePageSaga),
  ]);
}
